import { IFilter } from '@epicuro-next/ataraxia/models';
import { FilterQueryParams } from '@epicuro-next/models/products';
import { isSameFacetFilter } from '@epicuro-next/utilities';
import { Action, createReducer, on } from '@ngrx/store';
import {
  Filter,
  Product,
  SearchSortType,
  Sorting,
  SortingDirection,
} from 'graphql-api/gql-api-interfaces';

import {
  initialFilterQueryParams,
  initialFilterQueryParamsExceptQuery,
} from '../constants';

import * as productBasketActions from './product-basket.actions';

/**
 * P-Basket feature key
 */
export const PRODUCTBASKET_FEATURE_KEY = 'productBasket';

export const PBASKET_FEATURE_PRESET_VALUE_KEY = 'currentPresetValue';
export const PBASKET_CATALOG_ONLY_MODE_VALUE_KEY = 'catalogOnlyMode';
export const PBASKET_HORIZONTAL_VIEW_VALUE_KEY = 'horizontalView';
export const PBASKET_FILTERS_VISIBLE_VALUE_KEY = 'filtersVisible';

export const productDABKey = 'dab';

export const presetDefaultValue = 'any';

export const collapseDefaultValue = false;

/**
 * State interface
 */
export interface State {
  /**
   * has the p-basket loaded correctly the new page, or it's still loading
   */
  newPageLoaded: boolean;

  /**
   * has the p-basket loaded correctly the filters/products, or it's still loading
   */
  filtersLoaded: boolean;
  productsLoading: boolean;
  /**
   * list of products
   */
  products: Product[];

  /**
   * Query filters from URL
   */
  filterQuery: FilterQueryParams;

  /**
   * filter data
   */
  filters: Filter[];

  defaultFilters: Filter[];

  /** Available sorting option */
  sorting: Sorting[];

  /**
   * page number
   */
  page: number;

  /**
   * number of elements in a page
   */
  size: number;

  /**
   * Number of results
   */
  resultCount: number;

  /**
   * last activated filter
   */
  lastActivatedFilter?: IFilter;

  /**
   * Product load error message
   */
  productLoadError?: string;

  /**
   * Filter load error message
   */
  filterLoadError?: string;

  /**
   * Default preset filter value from rule engine
   */
  ruleEnginePresetValue?: string;

  /**
   * Default preset filter value
   */
  [PBASKET_FEATURE_PRESET_VALUE_KEY]?: string;

  exportSearchResult: {
    loading: boolean;
    error: string | null;
  };

  ruleEngineCollapseValue?: boolean;

  ruleEngineSortField: SearchSortType;
  ruleEngineSortDirection: SortingDirection;

  collapse?: boolean;
  orderedProductsBrandId?: string;

  [PBASKET_CATALOG_ONLY_MODE_VALUE_KEY]?: boolean;
  [PBASKET_HORIZONTAL_VIEW_VALUE_KEY]?: boolean;
  [PBASKET_FILTERS_VISIBLE_VALUE_KEY]?: boolean;
}

/**
 * The initial p-basket state
 */
export const initialState: State = {
  productsLoading: true,
  filterQuery: {
    ...initialFilterQueryParams,
  },
  filtersLoaded: false,
  newPageLoaded: true,
  products: [],
  filters: [],
  defaultFilters: [],
  sorting: [],
  page: 0,
  size: 25,
  resultCount: 0,
  exportSearchResult: {
    loading: false,
    error: null,
  },
  ruleEngineSortField: SearchSortType.Default,
  ruleEngineSortDirection: SortingDirection.Desc,
  [PBASKET_CATALOG_ONLY_MODE_VALUE_KEY]: false,
  [PBASKET_HORIZONTAL_VIEW_VALUE_KEY]: false,
  [PBASKET_FILTERS_VISIBLE_VALUE_KEY]: false,
};

export const rrpFilterId = 'retailPriceRange';
export const wspFilterId = 'wholesalePriceRange';
export const presetFilterId = 'preset';
export const isdDatesFilterId = 'isdDates';

// used to expose products order while search redirect from url resolver
export const orderedProductsBrandIdUrlParam = 'orderedProductsBrandId';

/**
 * Product basket reducer
 */
const productBasketReducer = createReducer(
  initialState,
  on(
    productBasketActions.loadProducts,
    (state, { clear = false }): State => ({
      ...state,
      page: clear ? 0 : state.page + 1,
      ...(!clear ? { newPageLoaded: false } : {}),
      resultCount: clear ? 0 : state.resultCount,
      productsLoading: true,
    }),
  ),
  on(
    productBasketActions.loadProductsSuccess,
    (state, { products, resultCount }): State => ({
      ...state,
      products: state.page > 0 ? [...state.products, ...products] : products,
      newPageLoaded: true,
      productsLoading: false,
      productLoadError: undefined,
      resultCount,
    }),
  ),
  on(
    productBasketActions.loadProductsFailure,
    (state, { error }): State => ({
      ...state,
      newPageLoaded: true,
      productLoadError: error,
      productsLoading: false,
    }),
  ),
  on(
    productBasketActions.updateProductsWithDiscounts,
    (state, { products }) => ({
      ...state,
      products,
    }),
  ),
  on(
    productBasketActions.setParamsFromURL,
    (state, { filters, preset, collapse, orderedProductsBrandId }): State => ({
      ...state,
      filterQuery: {
        ...filters,
        sortField: filters.sortField ?? state.ruleEngineSortField,
        sortDirection: filters.sortDirection ?? state.ruleEngineSortDirection,
      },
      currentPresetValue: preset ?? state.currentPresetValue,
      collapse:
        collapse ??
        state.collapse ??
        state.ruleEngineCollapseValue ??
        collapseDefaultValue,
      orderedProductsBrandId: orderedProductsBrandId ?? undefined,
    }),
  ),
  on(
    productBasketActions.loadFilters,
    (state): State => ({
      ...state,
      filtersLoaded: false,
      filterLoadError: undefined,
    }),
  ),
  on(
    productBasketActions.loadFiltersSuccess,
    (
      state,
      { filters, sorting, currentSearchQuery, previousSearchQuery },
    ): State => {
      const isSameFacetFilterCbFn = isSameFacetFilter(
        currentSearchQuery,
        previousSearchQuery,
      );
      const doNotReplaceSameFilters = filters.map((filter) => {
        const sameFacetFilterChange = isSameFacetFilterCbFn(filter);

        if (sameFacetFilterChange) {
          return (
            state.filters.find(
              (stateFilter) => stateFilter.fieldId === filter.fieldId,
            ) ?? filter
          );
        }

        return filter;
      });

      return {
        ...state,
        filters: [...doNotReplaceSameFilters],
        sorting,
        filtersLoaded: true,
        filterLoadError: undefined,
      };
    },
  ),
  on(
    productBasketActions.loadFiltersFailure,
    (state, { error }): State => ({
      ...state,
      filtersLoaded: true,
      filters: [],
      filterLoadError: error,
    }),
  ),
  on(productBasketActions.removeFiltersById, (state, { fieldId }): State => {
    const activeFilters = normalizeQueryFiltersToActiveFilters(
      state.filterQuery,
      state.currentPresetValue ?? presetDefaultValue,
    );
    const newActiveFilters = activeFilters.filter(
      (filter) => filter.fieldId !== fieldId,
    );
    const facets = denormalizeFacetsFromFiltersAndActiveFilters(
      state.filters,
      newActiveFilters,
    );
    const staticFilters = denormalizeStaticFiltersFromFiltersAndActiveFilters(
      state.filters,
      newActiveFilters,
    );

    const presetValue =
      fieldId === presetFilterId
        ? state.ruleEnginePresetValue ?? presetDefaultValue
        : null;

    return {
      ...state,
      filterQuery: denormalizeQueryFilters(
        state.filterQuery.q ?? '',
        facets,
        state.filterQuery.sortField,
        state.filterQuery.sortDirection,
        staticFilters,
      ),
      ...(presetValue && { currentPresetValue: presetValue }),
      orderedProductsBrandId: undefined,
    };
  }),
  on(
    productBasketActions.toggleFilter,
    (state, { fieldId, value, dataType, resetOtherFilters }): State => {
      const newFilter = { fieldId, value, dataType };
      // reset all filters while setting new preset
      if (newFilter.fieldId === presetFilterId) {
        return {
          ...state,
          filterQuery: {
            ...initialFilterQueryParams,
            sortField: state.filterQuery.sortField ?? state.ruleEngineSortField,
            sortDirection:
              state.filterQuery.sortDirection ?? state.ruleEngineSortDirection,
          },
          currentPresetValue: newFilter.value,
          orderedProductsBrandId: undefined,
        };
      }

      const activeFilters = normalizeQueryFiltersToActiveFilters(
        resetOtherFilters
          ? {
              ...initialFilterQueryParams,
              sortField:
                state.filterQuery.sortField ?? state.ruleEngineSortField,
              sortDirection:
                state.filterQuery.sortDirection ??
                state.ruleEngineSortDirection,
            }
          : state.filterQuery,
        state.currentPresetValue ?? presetDefaultValue,
      );

      const { activeFilters: newActiveFilters, lastActivatedFilter } =
        toggleFilterOfActiveFilters(activeFilters, state.filters, newFilter);

      const facets = denormalizeFacetsFromFiltersAndActiveFilters(
        state.filters,
        newActiveFilters,
      );

      const staticFilters = denormalizeStaticFiltersFromFiltersAndActiveFilters(
        state.filters,
        newActiveFilters,
      );

      return {
        ...state,
        lastActivatedFilter,
        filterQuery: denormalizeQueryFilters(
          state.filterQuery.q ?? '',
          facets,
          state.filterQuery.sortField,
          state.filterQuery.sortDirection,
          staticFilters,
        ),
        orderedProductsBrandId: undefined,
      };
    },
  ),
  on(productBasketActions.clearFilters, (state, { keepPreset }): State => {
    let currentPresetValue;
    let sortField;
    let sortDirection;
    if (keepPreset) {
      currentPresetValue = state.currentPresetValue;
      sortField = state.filterQuery.sortField;
      sortDirection = state.filterQuery.sortDirection;
    }

    return {
      ...state,
      filterQuery: {
        ...initialFilterQueryParams,
        sortField: sortField ?? state.ruleEngineSortField,
        sortDirection: sortDirection ?? state.ruleEngineSortDirection,
      },
      currentPresetValue:
        currentPresetValue ??
        state.ruleEnginePresetValue ??
        state.currentPresetValue ??
        presetDefaultValue,
      collapse:
        state.ruleEngineCollapseValue ?? state.collapse ?? collapseDefaultValue,
      orderedProductsBrandId: undefined,
    };
  }),
  on(
    productBasketActions.removeFilters,
    (state): State => ({
      ...state,
      filterQuery: {
        q: state.filterQuery.q,
        ...initialFilterQueryParamsExceptQuery,
        sortField: state.ruleEngineSortField,
        sortDirection: state.ruleEngineSortDirection,
      },
      currentPresetValue:
        state.ruleEnginePresetValue ??
        state.currentPresetValue ??
        presetDefaultValue,
      collapse:
        state.ruleEngineCollapseValue ?? state.collapse ?? collapseDefaultValue,
      orderedProductsBrandId: undefined,
    }),
  ),
  on(
    productBasketActions.resetFilters,
    (state): State => ({
      ...state,
      filterQuery: {
        ...initialFilterQueryParams,
        sortField: state.ruleEngineSortField,
        sortDirection: state.ruleEngineSortDirection,
      },
      orderedProductsBrandId: undefined,
    }),
  ),
  on(
    productBasketActions.setSearchQuery,
    productBasketActions.setSearchQuerySilent,
    (state, { query }): State => ({
      ...state,
      filterQuery: {
        ...state.filterQuery,
        q: query,
      },
      orderedProductsBrandId: undefined,
    }),
  ),
  on(
    productBasketActions.setSortingParams,
    (state, { sortField, sortDirection }): State => ({
      ...state,
      filterQuery: {
        ...state.filterQuery,
        sortField: sortField as SearchSortType,
        sortDirection,
      },
      orderedProductsBrandId: undefined,
    }),
  ),
  on(
    productBasketActions.initSortingParams,
    (state, { sortField, sortDirection }): State => ({
      ...state,
      filterQuery: {
        ...state.filterQuery,
        sortField: sortField as SearchSortType,
        sortDirection,
      },
      ruleEngineSortDirection: sortDirection,
      ruleEngineSortField: sortField as SearchSortType,
    }),
  ),
  on(
    productBasketActions.setRuleEnginePresetValue,
    (state, { defaultPreset, resetToDefaultPreset }): State => {
      const currentPresetValue =
        (resetToDefaultPreset ? defaultPreset : state.currentPresetValue) ??
        defaultPreset;
      return {
        ...state,
        ruleEnginePresetValue: defaultPreset,
        currentPresetValue,
      };
    },
  ),
  on(
    productBasketActions.setRuleEngineCollapseValue,
    (state, { defaultCollapse }): State => ({
      ...state,
      ruleEngineCollapseValue: defaultCollapse,
      collapse: state.collapse ?? defaultCollapse ?? collapseDefaultValue,
    }),
  ),
  on(productBasketActions.exportProductsSearchResult, (state) => ({
    ...state,
    exportSearchResult: {
      ...state.exportSearchResult,
      loading: true,
    },
  })),
  on(productBasketActions.exportProductsSearchResultSuccess, (state) => ({
    ...state,
    exportSearchResult: {
      ...state.exportSearchResult,
      loading: false,
    },
  })),
  on(
    productBasketActions.exportProductsSearchResultFailed,
    (state, { error }) => ({
      ...state,
      exportSearchResult: {
        loading: false,
        error,
      },
    }),
  ),
  on(productBasketActions.loadDefaultFiltersSuccess, (state, { filters }) => ({
    ...state,
    defaultFilters: filters,
  })),
  on(productBasketActions.setCollapseParam, (state, { collapse }) => {
    return {
      ...state,
      collapse,
    };
  }),
  on(productBasketActions.setSubsites, (state, { subsites }) => {
    return {
      ...state,
      filterQuery: {
        ...state.filterQuery,
        subsites,
      },
    };
  }),
  on(productBasketActions.setCatalogOnlyMode, (state, { value }) => ({
    ...state,
    [PBASKET_CATALOG_ONLY_MODE_VALUE_KEY]: value,
  })),

  on(productBasketActions.setHorizontalView, (state, { value }) => ({
    ...state,
    [PBASKET_HORIZONTAL_VIEW_VALUE_KEY]: value,
  })),

  on(productBasketActions.setFiltersVisible, (state, { value }) => ({
    ...state,
    [PBASKET_FILTERS_VISIBLE_VALUE_KEY]: value,
  })),
);

const parseFacetFilter = (f: string | null): IFilter[] => {
  if (f) {
    return f.split(',').reduce((aggr: IFilter[], facet: string) => {
      const facetSplitted = facet.split(':');

      const [fieldId, dataType, fieldValues] =
        facetSplitted.length === 3
          ? [facetSplitted[0], facetSplitted[1], facetSplitted[2]]
          : [facetSplitted[0], undefined, facetSplitted[1]];
      const values: string[] = fieldValues.split('|');
      return [
        ...aggr,
        ...values.map((value) => ({ fieldId, dataType, value })),
      ];
    }, []);
  }
  return [];
};

const parseRrpFilter = (rrp: string): IFilter[] =>
  rrp ? [{ fieldId: rrpFilterId, value: rrp }] : [];
const parseWspFilter = (wsp: string): IFilter[] =>
  wsp ? [{ fieldId: wspFilterId, value: wsp }] : [];
const parseMaxAgeFilter = (maxAge: string): IFilter[] =>
  maxAge ? [{ fieldId: 'maxAge', value: maxAge }] : [];
const parsePresetFilter = (preset: string | undefined): IFilter[] =>
  preset ? [{ fieldId: presetFilterId, value: preset }] : [];
const parseSubsitesFilter = (subsites: string | null) => {
  if (subsites && !Array.isArray(subsites)) {
    return subsites
      .split(',')
      .map((subsite) => ({ fieldId: 'subsite', value: subsite }));
  }
  return [];
};
const parseInventoryRange = (inventoryRange?: string) => {
  return inventoryRange
    ? [{ fieldId: 'inventoryRange', value: inventoryRange }]
    : [];
};

const parseIsdDateFilter = (idsDates?: string) => {
  return idsDates ? [{ fieldId: isdDatesFilterId, value: idsDates }] : [];
};

const parseAvailabilityFilter = (availability: string): IFilter[] =>
  availability
    ? availability
        .split(',')
        .map((value: string) => ({ fieldId: 'availability', value }))
    : [];

/**
 * Logic to normalize query filters to active filters
 */
export function normalizeQueryFiltersToActiveFilters(
  {
    availability,
    f,
    maxAge,
    rrp,
    wsp,
    subsites,
    inventoryRange,
    filterOutInbound,
    filterOutRecentlyDelivered,
    isdDates,
  }: FilterQueryParams,
  preset: string,
): IFilter[] {
  return [
    ...parseFacetFilter(f),
    ...parseRrpFilter(rrp),
    ...parseWspFilter(wsp),
    ...parseMaxAgeFilter(maxAge),
    ...parseSubsitesFilter(subsites),
    ...parseAvailabilityFilter(availability),
    ...parsePresetFilter(preset),
    ...parseInventoryRange(inventoryRange),
    ...parseIsdDateFilter(isdDates),
    ...(filterOutInbound
      ? [{ fieldId: 'filterOutInbound', value: filterOutInbound }]
      : []),
    ...(filterOutRecentlyDelivered
      ? [
          {
            fieldId: 'filterOutRecentlyDelivered',
            value: filterOutRecentlyDelivered,
          },
        ]
      : []),
  ];
}

/**
 * Logic to toggle filter
 */
export function toggleFilterOfActiveFilters(
  originalActiveFilters: IFilter[],
  filters: Filter[],
  { fieldId, value, dataType }: IFilter,
): {
  activeFilters: IFilter[];
  lastActivatedFilter: IFilter;
} {
  let activeFilters = [...originalActiveFilters];
  value.split('|').forEach((val) => {
    const filterPosition = activeFilters.findIndex(
      (filter) => filter.fieldId === fieldId && filter.value === val,
    );
    if (filterPosition > -1) {
      activeFilters.splice(filterPosition, 1);
    } else {
      /** if the filter is not multiple, remove any associated other value. */
      let filter = filters.find(
        (entry) =>
          entry.fieldId === fieldId ||
          entry.subFilters?.some((subFilter) => subFilter.fieldId === fieldId),
      );

      // set filter to subfilter if it's a grouped filter
      if (filter?.subFilters?.length) {
        filter = filter.subFilters.find(
          (subFilter) => subFilter.fieldId === fieldId,
        );
      }

      if (!filter?.isMultiple) {
        activeFilters = activeFilters.filter(
          (entry) => entry.fieldId !== filter?.fieldId,
        );
      }

      const maxAmountOfValues = filter?.maxAmountOfValues;
      if (
        maxAmountOfValues &&
        activeFilters.filter((x) => x.fieldId === fieldId)?.length >=
          maxAmountOfValues
      ) {
        // Delete based on fifo since new values come in 1 by 1
        activeFilters.splice(
          activeFilters.findIndex((x) => x.fieldId === fieldId),
          1,
        );
      }

      activeFilters.push({ fieldId, value: val, dataType });
      activeFilters.sort(sortByFieldId);
    }
  });
  return {
    activeFilters,
    lastActivatedFilter: { fieldId, value, dataType },
  };
}

const isPriceRangeFilter = (activeFilter: IFilter) =>
  activeFilter.fieldId === rrpFilterId || activeFilter.fieldId === wspFilterId;
const isNotPriceRangeFilter = (activeFilter: IFilter) =>
  !isPriceRangeFilter(activeFilter);
const isFacetFilter = (availableFilters: Filter[]) => (activeFilter: IFilter) =>
  availableFilters.find(({ fieldId }) => fieldId === activeFilter.fieldId)
    ?.isFacet || activeFilter.fieldId.startsWith('attributes');
const isNotFacetFilter =
  (availableFilters: Filter[]) => (activeFilter: IFilter) =>
    !isFacetFilter(availableFilters)(activeFilter);

function denormalizeFacetsFromFiltersAndActiveFilters(
  availableFilters: Filter[],
  activeFilters: IFilter[],
) {
  const activeFiltersGroupByFieldId = activeFilters
    .filter(isNotPriceRangeFilter)
    .filter(isFacetFilter(availableFilters))
    .reduce(
      (
        allGroupByFieldId: { [key in IFilter['fieldId']]: IFilter['value'][] },
        { fieldId, value }: IFilter,
      ) => ({
        ...allGroupByFieldId,
        [fieldId]: [value].concat(allGroupByFieldId[fieldId] ?? []),
      }),
      {},
    );

  return Object.keys(activeFiltersGroupByFieldId)
    .sort()
    .map((fieldId: IFilter['fieldId']) => {
      const dataType = availableFilters.find(
        (x) => x.fieldId === fieldId,
      )?.dataType;
      const activeFilterValues = activeFiltersGroupByFieldId[fieldId];
      const values = [...activeFilterValues].sort().join('|');

      if (dataType) {
        return `${fieldId}:${dataType}:${values}`;
      }
      return `${fieldId}:${values}`;
    })
    .join(',');
}

export function denormalizeStaticFiltersFromFiltersAndActiveFilters(
  availableFilters: Filter[],
  activeFilters: IFilter[],
) {
  const activeFiltersGroupByFieldId = activeFilters
    .filter(
      (activeFilter) =>
        isNotFacetFilter(availableFilters)(activeFilter) ||
        isPriceRangeFilter(activeFilter),
    )
    .reduce(
      (
        allGroupByFieldId: { [key in IFilter['fieldId']]: IFilter['value'][] },
        { fieldId, value }: IFilter,
      ) => ({
        ...allGroupByFieldId,
        [fieldId]: [value].concat(allGroupByFieldId[fieldId] ?? []),
      }),
      {},
    );

  return Object.keys(activeFiltersGroupByFieldId)
    .sort()
    .map((fieldId: IFilter['fieldId']) => ({
      [fieldId]: activeFiltersGroupByFieldId[fieldId].sort().join(','),
    }))
    .reduce((acc, currentValue) => ({ ...acc, ...currentValue }), {});
}

export function denormalizeQueryFilters(
  query: string,
  facets: string,
  sortField: SearchSortType,
  sortDirection: SortingDirection,
  staticFilters: { [key: string]: string },
): FilterQueryParams {
  return {
    q: query,
    f: facets,
    subsites: staticFilters.subsite,
    availability: staticFilters.availability,
    maxAge: staticFilters.maxAge,
    rrp: staticFilters.retailPriceRange,
    wsp: staticFilters.wholesalePriceRange,
    inventoryRange: staticFilters.inventoryRange,
    isdDates: staticFilters.isdDates,
    sortField,
    sortDirection,
    filterOutInbound: staticFilters.filterOutInbound,
    filterOutRecentlyDelivered: staticFilters.filterOutRecentlyDelivered,
  };
}

export function sortByFieldId(a: IFilter, b: IFilter) {
  return a.fieldId < b.fieldId ? -1 : 1;
}

/**
 * Reducer export
 * @param state The current state
 * @param action The action dispatched
 */
export function reducer(state: State | undefined, action: Action) {
  return productBasketReducer(state, action);
}
